import {
  GYM_AI_CAP_KEYS,
  type AdminGymAiLimits,
  type AdminGymAiLimitsUpdate,
  type GymAiCapKey,
} from '@steado/admin-contract/gyms';

import { humanise } from '@/lib/format';

/**
 * The gym AI's caps as the console words and checks them.
 *
 * `RANGES` repeats what `adminGymAiLimitsUpdateSchema` enforces, so the form
 * can say what is wrong before the API does. `gym-ai-limits.test.ts` parses
 * both ends of every range through the real schema, so the two cannot drift.
 */

export interface CapMeta {
  readonly label: string;
  /** One line under the field: what the cap covers. */
  readonly hint: string;
  readonly min: number;
  readonly max: number;
  /** Whether the number is tokens (which take "25M") or a plain count. */
  readonly unit: 'tokens' | 'drafts';
}

export const CAPS: Readonly<Record<GymAiCapKey, CapMeta>> = {
  monthlyTokens: {
    label: 'AI tokens this month',
    hint: 'Ask, What stands out and AI drafts share this.',
    min: 1,
    max: 1_000_000_000,
    unit: 'tokens',
  },
  askDailyTokens: {
    label: 'Ask tokens per person per day',
    hint: 'Counted for each trainer, and for the owner.',
    min: 1,
    max: 100_000_000,
    unit: 'tokens',
  },
  draftsPerMemberWeek: {
    label: 'AI drafts per member per week',
    hint: 'Any rolling 7 days. 0 turns AI drafts off.',
    min: 0,
    max: 50,
    unit: 'drafts',
  },
};

/** The gym AI tasks, as an operator knows them. */
const TASK_LABELS: Readonly<Record<string, string>> = {
  gym_ask: 'Ask',
  gym_insight: 'What stands out',
  plan_generation: 'AI drafts',
};
const TASK_ORDER = Object.keys(TASK_LABELS);

export function taskLabel(task: string): string {
  return TASK_LABELS[task] ?? humanise(task);
}

/** Known tasks in a fixed order, anything else after them. */
export function sortTasks<T extends { readonly task: string }>(rows: readonly T[]): T[] {
  const rank = (task: string): number => {
    const index = TASK_ORDER.indexOf(task);
    return index === -1 ? TASK_ORDER.length : index;
  };
  return [...rows].sort((a, b) => rank(a.task) - rank(b.task));
}

// --- Numbers ----------------------------------------------------------------

const GROUPED = new Intl.NumberFormat('en-US');

/** 25000000 as "25,000,000". */
export function formatCount(value: number): string {
  return GROUPED.format(value);
}

/** 12400000 as "12.4M", 25000000 as "25M", 320000 as "320k". */
export function compactTokens(value: number): string {
  const units: readonly (readonly [number, string])[] = [
    [1_000_000_000, 'B'],
    [1_000_000, 'M'],
    [1_000, 'k'],
  ];
  for (const [index, [size, suffix]] of units.entries()) {
    if (value < size) continue;
    const rounded = Math.round((value / size) * 10) / 10;
    // 999,960 rounds to "1000k"; call it 1M instead.
    const larger = units[index - 1];
    if (rounded >= 1000 && larger !== undefined) return `1${larger[1]}`;
    return `${String(rounded)}${suffix}`;
  }
  return String(value);
}

const SUFFIX_POWER: Readonly<Record<string, number>> = { k: 3, m: 6, b: 9 };

export type ParsedCount =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly reason: 'empty' | 'format' | 'fraction' };

/**
 * What an operator types for a cap: "25000000", "25,000,000", "25M", "1.5m",
 * "500k". Done on the digits rather than with floating point, so "1.1M" is
 * 1,100,000 and not 1,100,000.0000000002.
 */
export function parseCount(text: string): ParsedCount {
  const cleaned = text.replace(/[\s,_]/g, '').toLowerCase();
  if (cleaned === '') return { ok: false, reason: 'empty' };

  const match = /^(\d+)(?:\.(\d+))?([kmb])?$/.exec(cleaned);
  if (match === null) return { ok: false, reason: 'format' };

  const whole = match[1] ?? '';
  const fraction = match[2] ?? '';
  const suffix = match[3];
  if (suffix === undefined && fraction !== '') return { ok: false, reason: 'format' };

  const power = suffix === undefined ? 0 : (SUFFIX_POWER[suffix] ?? 0);
  let digits = whole + fraction;
  const shift = power - fraction.length;
  if (shift >= 0) {
    digits += '0'.repeat(shift);
  } else {
    // "1.2345k" has digits left over after the shift: only zeros may go.
    if (!/^0+$/.test(digits.slice(shift))) return { ok: false, reason: 'fraction' };
    digits = digits.slice(0, shift);
  }

  digits = digits.replace(/^0+(?=\d)/, '');
  if (digits.length > 12) return { ok: true, value: Number.MAX_SAFE_INTEGER };
  return { ok: true, value: Number(digits) };
}

// --- The form ---------------------------------------------------------------

export interface CapDraft {
  /** Take the environment's default: the request clears the override. */
  readonly useDefault: boolean;
  /** What is in the field; kept while "Use default" is on so a toggle back is not a retype. */
  readonly text: string;
}

export type LimitsDraft = Readonly<Record<GymAiCapKey, CapDraft>>;

export function draftOf(limits: AdminGymAiLimits): LimitsDraft {
  const draftFor = (cap: AdminGymAiLimits[GymAiCapKey]): CapDraft => ({
    useDefault: cap.override === null,
    text: formatCount(cap.override ?? cap.default),
  });
  return {
    monthlyTokens: draftFor(limits.monthlyTokens),
    askDailyTokens: draftFor(limits.askDailyTokens),
    draftsPerMemberWeek: draftFor(limits.draftsPerMemberWeek),
  };
}

/** What a draft means: an override, or null for the default. Null too when the field is not a valid number. */
function valueOf(key: GymAiCapKey, draft: CapDraft): number | null {
  if (draft.useDefault) return null;
  const parsed = parseCount(draft.text);
  const { min, max } = CAPS[key];
  return parsed.ok && parsed.value >= min && parsed.value <= max ? parsed.value : null;
}

/** What is wrong with a field, in words; null when it is fine. */
export function capIssue(key: GymAiCapKey, draft: CapDraft): string | null {
  if (draft.useDefault) return null;
  const meta = CAPS[key];
  const parsed = parseCount(draft.text);
  if (!parsed.ok) {
    if (parsed.reason === 'empty') return 'Enter a number, or use the default.';
    if (parsed.reason === 'fraction') return 'Use a whole number.';
    return meta.unit === 'tokens' ? 'Use digits, like 25,000,000 or 25M.' : 'Use a whole number.';
  }
  if (parsed.value < meta.min || parsed.value > meta.max) {
    return `Enter a number from ${formatCount(meta.min)} to ${formatCount(meta.max)}.`;
  }
  return null;
}

export interface CapChange {
  readonly key: GymAiCapKey;
  /** The override before; null is the default. */
  readonly from: number | null;
  /** The override after; null is the default. */
  readonly to: number | null;
}

/** The caps whose override differs from what the gym has now. Invalid fields count as unchanged. */
export function changesOf(limits: AdminGymAiLimits, draft: LimitsDraft): CapChange[] {
  const changes: CapChange[] = [];
  for (const key of GYM_AI_CAP_KEYS) {
    const field = draft[key];
    if (capIssue(key, field) !== null) continue;
    const to = valueOf(key, field);
    const from = limits[key].override;
    if (to !== from) changes.push({ key, from, to });
  }
  return changes;
}

export function hasIssues(draft: LimitsDraft): boolean {
  return GYM_AI_CAP_KEYS.some((key) => capIssue(key, draft[key]) !== null);
}

/** The PUT body: only what changed, `null` for a return to the default. */
export function toUpdateRequest(changes: readonly CapChange[]): AdminGymAiLimitsUpdate {
  const body: Partial<Record<GymAiCapKey, number | null>> = {};
  for (const change of changes) body[change.key] = change.to;
  return body;
}

/** "25,000,000 (default)" or "50,000,000 (this gym)". */
export function describeOverride(value: number | null, defaultValue: number): string {
  return value === null ? `${formatCount(defaultValue)} (default)` : `${formatCount(value)} (this gym)`;
}

/**
 * Said before saving, when the new cap is already used up: the change would
 * switch the AI off for someone at once, which is sometimes the point and
 * should never be a surprise.
 */
export function warningsFor(limits: AdminGymAiLimits, changes: readonly CapChange[]): string[] {
  const warnings: string[] = [];
  for (const change of changes) {
    const next = change.to ?? limits[change.key].default;
    if (change.key === 'monthlyTokens' && limits.monthlyTokens.used >= next) {
      warnings.push(
        `This gym has already used ${formatCount(limits.monthlyTokens.used)} tokens this month, so its AI stops at once.`,
      );
    }
    if (change.key === 'askDailyTokens') {
      const heaviest = limits.askDailyTokens.topToday[0];
      if (heaviest !== undefined && heaviest.tokens >= next) {
        warnings.push(`${heaviest.name} has already asked ${formatCount(heaviest.tokens)} tokens today.`);
      }
    }
  }
  return warnings;
}

// --- Usage ------------------------------------------------------------------

/** The API's own line for "near the limit" (`nearLimit`), for figures the API does not flag. */
const NEAR_LIMIT_SHARE = 0.8;

export type UsageLevel = 'ok' | 'near' | 'reached';

export function usageLevel(used: number, limit: number): UsageLevel {
  if (limit <= 0 || used >= limit) return 'reached';
  return used >= limit * NEAR_LIMIT_SHARE ? 'near' : 'ok';
}
